import React, { useMemo, useState } from 'react';
import { colors } from '../theme';
import {
  ACTION_BLUE, ACTION_BLUE_BG, ACTION_BLUE_BORDER,
  WAITING_GREEN, WAITING_GREEN_BG, WAITING_GREEN_BORDER,
  getRmaCustomerDisplayState
} from '../utils/rmaCustomerStatus';
import { formatDDMMYYYY } from '../utils/dates';

// customerAction: true means this step requires the customer to do something
const WORKFLOW_STEPS = [
  { key: 'pending', label: 'Request received', description: 'Your RMA request is received. We\'re reviewing it and will assign a return address shortly.' },
  { key: 'assigned', label: 'Ship your device', description: 'A return address has been assigned — please pack and ship your device to us.', customerAction: true },
  { key: 'received', label: 'Device received', description: 'We have received your device and are running initial intake checks.' },
  { key: 'awaiting_repair', label: 'Awaiting assessment', description: 'Your device has arrived and is in the queue for technical assessment.' },
  { key: 'repairing', label: 'In service', description: 'Our technicians are assessing your device. We will contact you if any action is required from your side.' },
  { key: 'awaiting_shipping', label: 'Ready to return', description: 'Service is complete. Please confirm your return shipping address so we can send your device back.', customerAction: true },
  { key: 'closed_case', label: 'Case closed', description: 'This case has been closed. Please contact North Invent if you have any questions.', hideUnlessActive: true },
  { key: 'shipped', label: 'On its way back', description: 'Your device has been shipped back to you. Expect delivery soon.' },
  { key: 'archived', label: 'Case closed & archived', description: 'Your case is complete and closed. Thank you for using North Invent service.' }
];


const STATUS_ALIASES = {
  Submitted: 'pending',
  Initiated: 'pending',
  Pending: 'pending',
  Assigned: 'assigned',
  Received: 'received',
  Assessment: 'awaiting_repair',
  'Awaiting Repair Start': 'awaiting_repair',
  'Waiting for Customer Approval': 'awaiting_repair',
  'Customer Approved Repair': 'awaiting_repair',
  Repairing: 'repairing',
  'Repair Started': 'repairing',
  'Repair In Progress': 'repairing',
  Ready: 'awaiting_shipping',
  'Repair Finished': 'awaiting_shipping',
  'Repair Finished (Back in Stock/Refurbished)': 'awaiting_shipping',
  'Awaiting Shipping Details': 'awaiting_shipping',
  'Awaiting Dispatch Confirmation': 'awaiting_shipping',
  Shipped: 'shipped',
  Closed: 'archived',
  ClosedCase: 'closed_case',
  closed_case: 'closed_case',
  ArchivedCaseClosed: 'archived',
  'Repair Cancelled by Customer': 'archived',
  cancelled: 'archived'
};

const normalizeStatus = status => {
  if (!status) return 'pending';
  const alias = STATUS_ALIASES[status];
  if (alias) return alias;
  const lowered = typeof status === 'string' ? status.toLowerCase() : status;
  return STATUS_ALIASES[lowered] || lowered || 'pending';
};

const formatDateTime = (value) => {
  if (!value) return null;
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}.${mm}.${yyyy}, ${hh}:${min}`;
  } catch (err) {
    return null;
  }
};

const formatCurrency = (value, currency = 'EUR') => {
  if (value === null || value === undefined) return null;
  const number = Number(value);
  if (Number.isNaN(number)) return String(value);
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0
  }).format(number);
};

const canonicalAccessoryCode = value => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  return trimmed
    .replace(/qty$/i, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/__+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();
};

const ACCESSORY_LABELS = {
  ac_power_cable: 'AC power cable',
  hdmi_cable: 'HDMI cable',
  dp_cable: 'DP cable',
  touch_cable: 'Touch cable (USB A–USB B)'
};

const formatAccessoryLabel = code => {
  const canonical = canonicalAccessoryCode(code);
  if (canonical && ACCESSORY_LABELS[canonical]) {
    return ACCESSORY_LABELS[canonical];
  }
  const source = typeof code === 'string' && code.trim() ? code.trim() : canonical;
  if (!source) return 'Accessory';
  return source.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
};

const formatMountingOption = value => {
  if (!value) return 'Not recorded';
  return value.replace(/_/g, ' ');
};

const formatHmiIncluded = value => {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  return 'Not recorded';
};

const formatAccessorySummary = items => {
  if (!Array.isArray(items) || items.length === 0) {
    return 'None recorded';
  }
  const counts = items.reduce((acc, entry) => {
    if (!entry) return acc;
    if (typeof entry === 'string') {
      const canonical = canonicalAccessoryCode(entry) || entry;
      acc[canonical] = (acc[canonical] || 0) + 1;
      return acc;
    }
    const canonical = canonicalAccessoryCode(entry.code || entry.token || '');
    if (!canonical) return acc;
    const qty = entry.included === false ? 0 : Math.max(0, Number(entry.qty || entry.quantity) || 0);
    if (qty === 0) return acc;
    acc[canonical] = (acc[canonical] || 0) + qty;
    return acc;
  }, {});
  const entries = Object.entries(counts);
  if (!entries.length) return 'None recorded';
  return entries
    .map(([canonical, qty]) => {
      const label = formatAccessoryLabel(canonical);
      return qty > 1 ? `${label} (x${qty})` : label;
    })
    .join(', ');
};

const formatMismatchFieldLabel = field => {
  if (!field) {
    return null;
  }
  if (field === 'mountingOption') {
    return 'mounting option';
  }
  if (field === 'hmiIncluded') {
    return 'HMI included';
  }
  if (field === 'mainDeviceSerial') {
    return 'main device serial';
  }
  if (field.startsWith('accessories:')) {
    const code = field.slice('accessories:'.length);
    return `accessories (${formatAccessoryLabel(code)})`;
  }
  return field.replace(/[_:.]+/g, ' ');
};

const receiptStyles = {
  container: {
    border: `1px solid ${colors.panelBorder}`,
    borderRadius: '14px',
    padding: '0.9rem',
    backgroundColor: colors.surfaceMuted,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '0.95rem',
    fontWeight: 600
  },
  meta: {
    fontSize: '0.75rem',
    color: colors.textDim
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.85rem'
  },
  label: {
    fontWeight: 600
  },
  accessoryRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    fontSize: '0.85rem'
  },
  accessoryDetail: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.2rem'
  },
  pending: {
    fontSize: '0.85rem',
    color: colors.textDim
  }
};

export default function RmaTimelineCard({
  rma,
  onSelect,
  showManage,
  onCustomerDecision,
  customerDecisionState,
  onDeclinePath,
  declinePathState
}) {
  const { steps, currentStatus, lastUpdate } = useMemo(() => {
    const history = Array.isArray(rma.statusHistory) ? rma.statusHistory : [];
    const normalizedHistory = history.map(entry => {
      const normalizedStatus = normalizeStatus(entry.status);
      return { ...entry, normalizedStatus };
    });

    const latestByStatus = {};
    normalizedHistory.forEach(entry => {
      const existing = latestByStatus[entry.normalizedStatus];
      const entryDate = entry.date ? new Date(entry.date) : null;
      const hasBetterDate = entryDate && (!existing?.date || entryDate > new Date(existing.date));
      if (!existing || hasBetterDate) {
        latestByStatus[entry.normalizedStatus] = entry;
      }
    });

    const normalizedCurrent = normalizeStatus(rma.status);
    const lastTimelineUpdate = normalizedHistory.length ? normalizedHistory[normalizedHistory.length - 1].date : rma.updatedAt || rma.submittedAt;

    const computedSteps = WORKFLOW_STEPS.map(step => ({
      ...step,
      entry: latestByStatus[step.key] || null
    }));

    return {
      steps: computedSteps,
      currentStatus: normalizedCurrent,
      lastUpdate: lastTimelineUpdate
    };
  }, [rma.status, rma.statusHistory, rma.submittedAt, rma.updatedAt]);

  const statusLabel = WORKFLOW_STEPS.find(step => step.key === currentStatus)?.label || currentStatus;
  const repairData = rma.repair && typeof rma.repair === 'object' ? rma.repair : {};
  const repairPhase = repairData.phase === 'execution' ? 'execution' : 'inspection';
  const repairStartedAt = formatDateTime(repairData.startedAt);
  const repairFinalizedAt = repairData.finalizedAt ? formatDDMMYYYY(repairData.finalizedAt) : null;
  const actualRepairDays = useMemo(() => {
    if (!repairData.startedAt || !repairData.finalizedAt) return null;
    const start = new Date(repairData.startedAt);
    const end = new Date(repairData.finalizedAt);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
    return Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)));
  }, [repairData.startedAt, repairData.finalizedAt]);
  const warrantyDecision = repairData.warranty?.decision || null;
  const warrantyNote = repairData.warranty?.note || '';
  const warrantyAccepted = warrantyDecision === 'accepted';
  const repairTasks = Array.isArray(repairData.tasks) ? repairData.tasks : [];
  const hasRepairTasks = repairTasks.length > 0;
  const repairSummary = repairData.summary || '';
  const showAwaitingRepairMessage = currentStatus === 'awaiting_repair';
  const showRepairDetails = currentStatus === 'repairing' || currentStatus === 'awaiting_shipping';

  const customerApproval =
    repairData.customerApproval && typeof repairData.customerApproval === 'object'
      ? repairData.customerApproval
      : {};
  const approvalRequired = customerApproval.required === true;
  const approvalStatus = customerApproval.status || 'pending';
  const customerDeclined = approvalStatus === 'declined';
  const postDecline =
    rma.postDecline && typeof rma.postDecline === 'object' ? rma.postDecline : {};
  const postDeclineChoice = postDecline.choice || null;
  const declineSelectedReturn = postDeclineChoice === 'return_as_is';
  const declineSelectedClose = postDeclineChoice === 'retain_and_close';
  const uiState = rma.ui && typeof rma.ui === 'object' ? rma.ui : {};
  const isDecommissioned = uiState.decommissioned === true;

  const rawChallenges = Array.isArray(repairData.challenges) ? repairData.challenges.filter(Boolean) : [];
  const sortedChallenges = [...rawChallenges].sort((a, b) => {
    const roundA = Number(a?.round) || 0;
    const roundB = Number(b?.round) || 0;
    return roundA - roundB;
  });
  const latestChallenge = sortedChallenges.length > 0 ? sortedChallenges[sortedChallenges.length - 1] : null;
  const latestChallengeRound = Number(latestChallenge?.round) || Number(repairData.challengeRound) || 0;
  const latestChallengeSubmittedAt = latestChallenge?.submittedAt ? formatDateTime(latestChallenge.submittedAt) : null;
  const latestChallengeReview = latestChallenge?.review || null;
  const latestChallengeOutcome = latestChallengeReview?.outcome || '';
  const challengePending = Boolean(latestChallenge && !latestChallengeOutcome);
  const challengeDeclined = latestChallengeOutcome === 'declined';
  const challengeAccepted = latestChallengeOutcome === 'accepted';
  const isChallenged = approvalStatus === 'challenged' || challengePending;
  const customerHasResponded = approvalRequired && !['pending', 'challenged'].includes(approvalStatus);

  const [hovered, setHovered] = useState(false);


  const formattedLastUpdate = formatDateTime(lastUpdate) || 'N/A';
  const quoteAmount = repairData.quote?.amount;
  const formattedQuote =
    quoteAmount === undefined || quoteAmount === null ? null : formatCurrency(quoteAmount, 'EUR');
  const timing = repairData.timing && typeof repairData.timing === 'object' ? repairData.timing : {};
  const repairDays = typeof timing.repairDays === 'number' && timing.repairDays > 0 ? timing.repairDays : null;
  const partsInfo = timing.parts && typeof timing.parts === 'object' ? timing.parts : {};
  const partsInStock = partsInfo.inStock === false ? false : true;
  const partsEta = partsInfo.etaDate ? formatDDMMYYYY(partsInfo.etaDate) : '';

  const expectedCompletionDisplay = timing.expectedCompletion ? formatDDMMYYYY(timing.expectedCompletion) : '';
  const challengeRoundText = latestChallengeRound ? ` (Round #${latestChallengeRound})` : '';
  const shouldShowQuote =
    Boolean(formattedQuote) &&
    !warrantyAccepted &&
    !declineSelectedReturn &&
    !declineSelectedClose &&
    approvalStatus !== 'accepted' &&
    repairPhase !== 'execution';
  let approvalBanner = null;
  if (declineSelectedClose || isDecommissioned || currentStatus === 'closed_case') {
    approvalBanner = (
      <div
        style={{
          backgroundColor: 'rgba(236, 33, 36, 0.18)',
          color: '#ffd9d9',
          border: '1px solid rgba(236, 33, 36, 0.24)',
          borderRadius: '8px',
          padding: '0.6rem 0.75rem',
          fontSize: '0.85rem'
        }}
      >
        Repair declined — case closed and device will be decommissioned.
      </div>
    );
  } else if (declineSelectedReturn) {
    approvalBanner = (
      <div
        style={{
          backgroundColor: 'rgba(236, 33, 36, 0.12)',
          color: colors.textStrong,
          border: '1px solid rgba(236, 33, 36, 0.18)',
          borderRadius: '8px',
          padding: '0.6rem 0.75rem',
          fontSize: '0.85rem'
        }}
      >
        Repair declined — device will be returned as-is once shipping is arranged.
      </div>
    );
  } else if (customerDeclined || rma.status === 'canceled_by_customer') {
    approvalBanner = (
      <div
        style={{
          backgroundColor: 'rgba(236, 33, 36, 0.12)',
          color: colors.textStrong,
          border: '1px solid rgba(236, 33, 36, 0.18)',
          borderRadius: '8px',
          padding: '0.6rem 0.75rem',
          fontSize: '0.85rem'
        }}
      >
        Repair declined — choose how to proceed below.
      </div>
    );
  } else if (approvalStatus === 'accepted' && !challengeAccepted) {
    approvalBanner = (
      <div
        style={{
          backgroundColor: 'rgba(70, 187, 137, 0.2)',
          color: colors.textStrong,
          border: '1px solid rgba(70, 187, 137, 0.24)',
          borderRadius: '8px',
          padding: '0.6rem 0.75rem',
          fontSize: '0.85rem'
        }}
      >
        Accepted — proceeding with repair (Stage 2/2).
      </div>
    );
  } else if (challengePending) {
    approvalBanner = (
      <div
        style={{
          backgroundColor: 'rgba(255, 193, 7, 0.12)',
          color: colors.textStrong,
          border: '1px solid rgba(255, 193, 7, 0.18)',
          borderRadius: '8px',
          padding: '0.6rem 0.75rem',
          fontSize: '0.85rem'
        }}
      >
        Customer challenge submitted{challengeRoundText}
        {latestChallengeSubmittedAt ? ` — ${latestChallengeSubmittedAt}` : ''}. Our team will review your information.
      </div>
    );
  } else if (challengeDeclined) {
    approvalBanner = (
      <div
        style={{
          backgroundColor: 'rgba(236, 33, 36, 0.12)',
          color: colors.textStrong,
          border: '1px solid rgba(236, 33, 36, 0.18)',
          borderRadius: '8px',
          padding: '0.6rem 0.75rem',
          fontSize: '0.85rem'
        }}
      >
        Your previous challenge{challengeRoundText} was declined. You may accept the quoted repair, decline, or submit another challenge.
      </div>
    );
  } else if (challengeAccepted) {
    approvalBanner = (
      <div
        style={{
          backgroundColor: 'rgba(70, 187, 137, 0.2)',
          color: colors.textStrong,
          border: '1px solid rgba(70, 187, 137, 0.24)',
          borderRadius: '8px',
          padding: '0.6rem 0.75rem',
          fontSize: '0.85rem'
        }}
      >
        Warranty accepted by NIN — proceeding to repair.
      </div>
    );
  } else if (approvalRequired && !customerHasResponded) {
    approvalBanner = (
      <div
        style={{
          backgroundColor: 'rgba(255, 193, 7, 0.12)',
          color: colors.textStrong,
          border: '1px solid rgba(255, 193, 7, 0.18)',
          borderRadius: '8px',
          padding: '0.6rem 0.75rem',
          fontSize: '0.85rem'
        }}
      >
        Waiting for customer decision.
      </div>
    );
  }

  const { isActionRequired, isWaitingOnNI, cardBorder } = getRmaCustomerDisplayState(rma);

  const receipt = rma.receipt && typeof rma.receipt === 'object' ? rma.receipt : null;
  const receiptExpected = receipt?.expected || null;
  const receiptFound = receipt?.found || null;
  const receiptMismatch = receipt?.mismatch || null;
  const receiptPending = !receipt || !receipt.confirmedAt;
  const receiptConfirmedAt = receipt?.confirmedAt ? formatDateTime(receipt.confirmedAt) : null;

  // Use rma.expectedReturn.accessories as authoritative source for what the customer declared.
  // receipt.expected.accessories may be stored incorrectly (all-zero) due to a Mongoose
  // subdocument serialization issue on older records — always prefer the source of truth.
  const authoritativeExpectedAccMap = useMemo(() => {
    const acc = rma.expectedReturn?.accessories;
    if (!acc || typeof acc !== 'object') return {};
    const map = {};
    Object.entries(acc).forEach(([key, qty]) => {
      const canonical = canonicalAccessoryCode(key);
      if (canonical && (Number(qty) || 0) > 0) {
        map[canonical] = Number(qty);
      }
    });
    return map;
  }, [rma.expectedReturn]);

  const authoritativeFoundAccMap = useMemo(() => {
    const acc = Array.isArray(receiptFound?.accessories) ? receiptFound.accessories : [];
    const map = {};
    acc.forEach(entry => {
      if (!entry || entry.included === false) return;
      const canonical = canonicalAccessoryCode(entry.code || '');
      const qty = Math.max(0, Number(entry.qty) || 0);
      if (canonical && qty > 0) map[canonical] = (map[canonical] || 0) + qty;
    });
    return map;
  }, [receiptFound]);

  // Recompute accessory mismatch from authoritative sources
  const recomputedAccessoryMismatchFields = useMemo(() => {
    const allCodes = new Set([
      ...Object.keys(authoritativeExpectedAccMap),
      ...Object.keys(authoritativeFoundAccMap)
    ]);
    const fields = [];
    allCodes.forEach(code => {
      if ((authoritativeExpectedAccMap[code] || 0) !== (authoritativeFoundAccMap[code] || 0)) {
        fields.push(`accessories:${code}`);
      }
    });
    return fields;
  }, [authoritativeExpectedAccMap, authoritativeFoundAccMap]);

  // Non-accessory mismatch fields from the stored receipt (mounting, serial, etc.)
  const nonAccessoryStoredFields = Array.isArray(receiptMismatch?.fields)
    ? receiptMismatch.fields.filter(f => !f.startsWith('accessories:'))
    : [];
  const effectiveMismatchFields = [...nonAccessoryStoredFields, ...recomputedAccessoryMismatchFields];
  const effectiveHasMismatch = effectiveMismatchFields.length > 0;
  const mismatchFieldLabels = effectiveMismatchFields.map(formatMismatchFieldLabel).filter(Boolean);

  const receiptAccessoriesExpected = Object.keys(authoritativeExpectedAccMap).length > 0
    ? Object.entries(authoritativeExpectedAccMap)
        .map(([canonical, qty]) => qty > 1 ? `${formatAccessoryLabel(canonical)} (x${qty})` : formatAccessoryLabel(canonical))
        .join(', ')
    : 'None declared';
  const receiptAccessoriesFound = formatAccessorySummary(
    Array.isArray(receiptFound?.accessories) ? receiptFound.accessories : []
  );

  const handleCardClick = () => {
    if (typeof onSelect === 'function') {
      onSelect();
    }
  };

  return (
    <div
      onClick={handleCardClick}
      onMouseEnter={() => onSelect && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        backgroundColor: colors.surface,
        borderRadius: '18px',
        padding: '1.25rem',
        boxShadow: isActionRequired
          ? hovered && onSelect
            ? `0 0 0 2px ${ACTION_BLUE_BORDER}, 0 6px 24px rgba(59,130,246,0.18)`
            : `0 0 0 2px ${ACTION_BLUE_BORDER}, ${colors.shadow}`
          : isWaitingOnNI
          ? hovered && onSelect
            ? `0 0 0 2px ${WAITING_GREEN_BORDER}, 0 6px 24px rgba(70,187,137,0.12)`
            : `0 0 0 1px ${WAITING_GREEN_BORDER}, ${colors.shadow}`
          : hovered && onSelect
          ? `0 0 0 2px rgba(70,187,137,0.5), 0 6px 24px rgba(70,187,137,0.12)`
          : colors.shadow,
        border: hovered && onSelect && !isActionRequired && !isWaitingOnNI ? '1px solid rgba(70,187,137,0.4)' : cardBorder,
        background: isActionRequired ? `${ACTION_BLUE_BG}, ${colors.surface}` : isWaitingOnNI ? `${WAITING_GREEN_BG}, ${colors.surface}` : colors.surface,
        cursor: onSelect ? 'pointer' : 'default',
        transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
        transform: hovered && onSelect ? 'translateY(-2px)' : undefined,
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        color: colors.textBody
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: '1.05rem', color: colors.textStrong }}>
            {rma.rmaId}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            {isActionRequired ? (
              <span style={{
                fontSize: '0.78rem', fontWeight: 700, color: ACTION_BLUE,
                background: ACTION_BLUE_BG, border: `1px solid ${ACTION_BLUE_BORDER}`,
                borderRadius: '999px', padding: '0.2rem 0.65rem', letterSpacing: '0.03em'
              }}>
                Action required
              </span>
            ) : isWaitingOnNI ? (
              <span style={{
                fontSize: '0.78rem', fontWeight: 600, color: WAITING_GREEN,
                background: WAITING_GREEN_BG, border: `1px solid ${WAITING_GREEN_BORDER}`,
                borderRadius: '999px', padding: '0.2rem 0.65rem', letterSpacing: '0.03em'
              }}>
                Waiting on North Invent
              </span>
            ) : (
              <span style={{ color: colors.accent, fontWeight: 600, fontSize: '0.9rem' }}>{statusLabel}</span>
            )}
            {onSelect && (
              <span style={{ color: colors.textMuted, fontSize: '0.85rem', opacity: 0.6 }}>›</span>
            )}
          </div>
        </div>
        <div style={{ color: colors.textDim, fontSize: '0.85rem' }}>Last update: {formattedLastUpdate}</div>
        {approvalBanner}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={receiptStyles.container}>
          {receiptPending ? (
            <div style={receiptStyles.pending}>Intake checks pending.</div>
          ) : (
            <>
              <div style={receiptStyles.header}>
                <span>Intake</span>
                <span
                  style={{
                    color: effectiveHasMismatch ? colors.statusYellow : colors.success,
                    fontWeight: 600
                  }}
                >
                  {effectiveHasMismatch ? 'Mismatch' : 'Match'}
                </span>
              </div>
              {receiptConfirmedAt && (
                <div style={receiptStyles.meta}>Confirmed {receiptConfirmedAt}</div>
              )}
              {effectiveHasMismatch && (
                <>
                  {mismatchFieldLabels.length > 0 && (
                    <div style={receiptStyles.meta}>
                      Differences: {mismatchFieldLabels.join(', ')}
                    </div>
                  )}
                  <div style={receiptStyles.row}>
                    <span style={receiptStyles.label}>Mounting</span>
                    <span>{formatMountingOption(receiptExpected?.mountingOption)} → {formatMountingOption(receiptFound?.mountingOption)}</span>
                  </div>
                  <div style={receiptStyles.row}>
                    <span style={receiptStyles.label}>HMI</span>
                    <span>{formatHmiIncluded(receiptExpected?.hmiIncluded)} → {formatHmiIncluded(receiptFound?.hmiIncluded)}</span>
                  </div>
                  <div style={receiptStyles.accessoryRow}>
                    <span style={receiptStyles.label}>Accessories</span>
                    <div style={receiptStyles.accessoryDetail}>
                      <div><strong>Expected:</strong> {receiptAccessoriesExpected}</div>
                      <div><strong>Found:</strong> {receiptAccessoriesFound}</div>
                    </div>
                  </div>
                  {receiptMismatch?.summary && (
                    <div style={receiptStyles.meta}>{receiptMismatch.summary}</div>
                  )}
                </>
              )}
              {!effectiveHasMismatch && (
                <div style={receiptStyles.meta}>All received items matched your declaration.</div>
              )}
              {(receipt?.missingItems || []).length > 0 && (
                <div style={{ ...receiptStyles.meta, color: colors.statusYellow }}>
                  Not included in the shipment: {receipt.missingItems.map(item => `${item.name}${item.qty > 1 ? ` ×${item.qty}` : ''}`).join(', ')}
                </div>
              )}
            </>
          )}
        </div>

        {steps.filter(step => !step.hideUnlessActive || step.key === currentStatus).map((step, index) => {
          const hasEntry = Boolean(step.entry);
          const isCurrent = step.key === currentStatus;
          const isCancelledPath = step.branch && (rma.customerDecision === 'declined' || currentStatus === step.key);

          // A non-branch step is implicitly completed if the workflow has moved past it,
          // even without an explicit status history entry.
          const currentStepIndex = steps.findIndex(s => s.key === currentStatus);
          const isPastStep = !step.branch && !step.optional && index < currentStepIndex;
          const isCompleted = hasEntry || isPastStep;

          const timestamp = formatDateTime(step.entry?.date);
          const note = step.entry?.note;
          const updatedBy = null;

          const isStepActionRequired =
            isCurrent && (
              (step.customerAction &&
                !(step.key === 'assigned' && isWaitingOnNI) &&
                step.key !== 'awaiting_shipping'
              ) ||
              (step.key === 'awaiting_shipping' && isActionRequired)
            );

          const isCurrentWaitingOnNI = isCurrent && isWaitingOnNI && step.key === currentStatus;
          const bulletColor = isCancelledPath
            ? colors.danger
            : isStepActionRequired
            ? ACTION_BLUE
            : isCompleted || isCurrentWaitingOnNI
            ? colors.accent
            : isCurrent
            ? colors.statusYellow
            : colors.textDim;

          return (
            <div key={step.key} style={{ display: 'flex', gap: '0.75rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <span
                  style={{
                    width: '14px',
                    height: '14px',
                    borderRadius: '50%',
                    backgroundColor: bulletColor,
                    marginTop: '4px'
                  }}
                />
                {index < steps.length - 1 && (
                  <span
                    style={{
                      flexGrow: 1,
                      width: '2px',
                      background: 'linear-gradient(180deg, rgba(70,187,137,0.2) 0%, rgba(70,187,137,0) 100%)',
                      minHeight: '32px'
                    }}
                  />
                )}
              </div>
              <div style={{ flex: 1, marginBottom: '0.5rem' }}>
                <div style={{
                  fontWeight: 600,
                  color: isCancelledPath ? colors.danger : isStepActionRequired ? ACTION_BLUE : (isCompleted || isCurrent ? colors.textStrong : colors.textDim)
                }}>
                  {step.label}
                  {isStepActionRequired && (
                    <span style={{ marginLeft: '0.4rem', fontSize: '0.76rem', color: ACTION_BLUE, fontWeight: 700 }}>← your turn</span>
                  )}
                </div>
                <div style={{ fontSize: '0.85rem', color: colors.textDim }}>
                  {timestamp
                    ? `Updated ${timestamp}`
                    : (step.key === 'awaiting_shipping' && isWaitingOnNI)
                    ? 'Your address is confirmed. North Invent is preparing your shipment.'
                    : step.description}
                  {updatedBy && ` - ${updatedBy}`}
                </div>
                {note && (
                  <div style={{ marginTop: '0.25rem', fontSize: '0.85rem', color: colors.textBody }}>
                    {note}
                  </div>
                )}
                {!isCompleted && !timestamp && step.branch && (
                  <div style={{ marginTop: '0.25rem', fontSize: '0.8rem', color: colors.textDim }}>
                    This path is only used if the repair is cancelled.
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showRepairDetails && (warrantyDecision || approvalRequired || formattedQuote || repairDays !== null || expectedCompletionDisplay) && (
        <div
          style={{
            backgroundColor: 'rgba(70,187,137,0.08)',
            borderRadius: '14px',
            padding: '1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            border: '1px solid rgba(70,187,137,0.18)'
          }}
        >
          <div style={{ fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.textMuted }}>Service Plan</div>

          {/* Key figures row */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
            {warrantyDecision && (
              <div style={{
                display: 'flex', flexDirection: 'column', gap: '0.15rem',
                padding: '0.5rem 0.75rem', borderRadius: '8px',
                backgroundColor: warrantyDecision === 'accepted' ? 'rgba(70,187,137,0.12)' : 'rgba(248,113,113,0.1)',
                border: `1px solid ${warrantyDecision === 'accepted' ? 'rgba(70,187,137,0.3)' : 'rgba(248,113,113,0.3)'}`
              }}>
                <span style={{ fontSize: '0.72rem', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Warranty</span>
                <span style={{ fontWeight: 700, fontSize: '0.95rem', color: warrantyDecision === 'accepted' ? colors.accent : '#f87171' }}>
                  {warrantyDecision === 'accepted' ? 'Covered' : 'Not covered'}
                </span>
              </div>
            )}
            <div style={{
              display: 'flex', flexDirection: 'column', gap: '0.15rem',
              padding: '0.5rem 0.75rem', borderRadius: '8px',
              backgroundColor: actualRepairDays !== null ? 'rgba(70,187,137,0.07)' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${actualRepairDays !== null ? 'rgba(70,187,137,0.25)' : 'rgba(255,255,255,0.08)'}`
            }}>
              <span style={{ fontSize: '0.72rem', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {actualRepairDays !== null ? 'Service time' : 'Est. service time'}
              </span>
              <span style={{ fontWeight: 700, fontSize: '0.95rem', color: actualRepairDays !== null ? colors.accent : colors.textStrong }}>
                {actualRepairDays !== null ? `${actualRepairDays} days` : (repairDays !== null ? `${repairDays} days` : 'Under review')}
              </span>
              {actualRepairDays !== null && repairDays !== null && (
                <span style={{ fontSize: '0.75rem', color: colors.textDim, marginTop: '0.1rem' }}>
                  Est. {repairDays} days
                </span>
              )}
            </div>
            {(repairFinalizedAt || expectedCompletionDisplay) && (
              <div style={{
                display: 'flex', flexDirection: 'column', gap: '0.15rem',
                padding: '0.5rem 0.75rem', borderRadius: '8px',
                backgroundColor: repairFinalizedAt ? 'rgba(70,187,137,0.07)' : 'rgba(99,179,237,0.08)',
                border: `1px solid ${repairFinalizedAt ? 'rgba(70,187,137,0.25)' : 'rgba(99,179,237,0.25)'}`
              }}>
                <span style={{ fontSize: '0.72rem', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {repairFinalizedAt ? 'Completed' : 'Est. completion'}
                </span>
                <span style={{ fontWeight: 700, fontSize: '0.95rem', color: repairFinalizedAt ? colors.accent : '#63b3ed' }}>
                  {repairFinalizedAt || expectedCompletionDisplay}
                </span>
              </div>
            )}
            {shouldShowQuote && (
              <div style={{
                display: 'flex', flexDirection: 'column', gap: '0.15rem',
                padding: '0.5rem 0.75rem', borderRadius: '8px',
                backgroundColor: 'rgba(251,146,60,0.08)',
                border: '1px solid rgba(251,146,60,0.25)'
              }}>
                <span style={{ fontSize: '0.72rem', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Quote</span>
                <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#fb923c' }}>{formattedQuote}</span>
              </div>
            )}
          </div>

          {/* Supporting detail lines */}
          <div style={{ fontSize: '0.85rem', color: colors.textMuted, display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
            {warrantyDecision === 'accepted' && (
              <span>Covered under warranty — no additional cost.</span>
            )}
            {!partsInStock && partsEta && (
              <span>Parts on order — arriving {partsEta}.</span>
            )}
          </div>
          {approvalRequired && approvalStatus === 'pending' && (
            <div style={{
              marginTop: '0.25rem',
              borderRadius: '10px',
              border: '1px solid rgba(99,179,237,0.3)',
              backgroundColor: 'rgba(99,179,237,0.06)',
              padding: '0.75rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem'
            }}>
              <div style={{ fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.07em', color: '#63b3ed' }}>
                Your decision is required
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                <div style={{ padding: '0.35rem 0.65rem', borderRadius: '6px', backgroundColor: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.25)', fontSize: '0.82rem', color: '#f87171', fontWeight: 600 }}>
                  Warranty: Not covered
                </div>
                {formattedQuote && (
                  <div style={{ padding: '0.35rem 0.65rem', borderRadius: '6px', backgroundColor: 'rgba(251,146,60,0.1)', border: '1px solid rgba(251,146,60,0.25)', fontSize: '0.82rem', color: '#fb923c', fontWeight: 600 }}>
                    Quoted: {formattedQuote}
                  </div>
                )}
                {repairDays && (
                  <div style={{ padding: '0.35rem 0.65rem', borderRadius: '6px', backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', fontSize: '0.82rem', color: colors.textMuted, fontWeight: 600 }}>
                    Est. {repairDays} days
                  </div>
                )}
              </div>
              <div style={{ fontSize: '0.82rem', color: '#63b3ed' }}>
                Open case details to accept, decline, or challenge the warranty decision.
              </div>
            </div>
          )}
          {customerHasResponded && !isChallenged && (
            <div
              style={{
                fontSize: '0.85rem',
                color: declineSelectedClose || isDecommissioned
                  ? colors.danger
                  : declineSelectedReturn
                  ? colors.textStrong
                  : customerDeclined
                  ? colors.textStrong
                  : colors.accent
              }}
            >
              {declineSelectedClose || isDecommissioned
                ? 'Repair declined — case closed.'
                : declineSelectedReturn
                ? 'Repair declined — returning device as-is.'
                : customerDeclined
                ? 'Repair declined.'
                : 'Repair accepted — work in progress.'}
            </div>
          )}
          {warrantyNote && !challengeAccepted && (
            <div style={{ fontSize: '0.85rem', color: colors.textDim }}>{warrantyNote}</div>
          )}
        </div>
      )}

      {(showAwaitingRepairMessage || showRepairDetails || hasRepairTasks || repairSummary) && (
        <div
          style={{
            borderTop: `1px solid ${colors.panelBorder}`,
            paddingTop: '0.75rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.6rem'
          }}
        >
          <div style={{ fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.textMuted }}>Service Progress</div>
          {showAwaitingRepairMessage && (
            <div style={{ fontSize: '0.9rem', color: colors.textMuted }}>Awaiting technician to start service.</div>
          )}
          {showRepairDetails && (
            <div style={{ fontSize: '0.9rem', color: colors.textBody, display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
              <div>
                <span style={{ color: colors.textMuted }}>Started: </span>
                <span style={{ fontWeight: 600, color: colors.textStrong }}>{repairStartedAt || 'Not recorded'}</span>
              </div>
              {currentStatus === 'awaiting_shipping' && (
                <div style={{ color: colors.accent, fontWeight: 600 }}>Service complete — preparing shipment.</div>
              )}
            </div>
          )}
          {hasRepairTasks && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Tasks</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {repairTasks.map((task, index) => {
                  const taskLabel = task?.label ? task.label : `Task ${index + 1}`;
                  const taskNote = task?.note ? String(task.note) : '';
                  const done = Boolean(task?.done);
                  return (
                    <div key={`${taskLabel}-${index}`} style={{
                      display: 'flex', gap: '0.6rem', alignItems: 'flex-start',
                      padding: '0.5rem 0.65rem', borderRadius: '8px',
                      backgroundColor: done ? 'rgba(70,187,137,0.07)' : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${done ? 'rgba(70,187,137,0.2)' : 'rgba(255,255,255,0.07)'}`
                    }}>
                      <span style={{ marginTop: '0.05rem', color: done ? colors.accent : colors.textDim, fontWeight: 700, fontSize: '0.9rem' }}>
                        {done ? '✓' : '○'}
                      </span>
                      <span style={{ flex: 1 }}>
                        <span style={{ fontWeight: 600, fontSize: '0.9rem', color: done ? colors.textStrong : colors.textBody }}>{taskLabel}</span>
                        {taskNote && (
                          <span style={{ display: 'block', fontSize: '0.82rem', color: colors.textDim, marginTop: '0.1rem' }}>{taskNote}</span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {repairSummary && (
            <div style={{ fontSize: '0.88rem', color: colors.textMuted }}>
              <span style={{ fontWeight: 600, color: colors.textBody }}>Summary: </span>{repairSummary}
            </div>
          )}
        </div>
      )}

      {showManage && onSelect && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={(event) => {
              event.stopPropagation();
              onSelect();
            }}
            style={{
              padding: '0.4rem 0.75rem',
              borderRadius: '999px',
              border: `1px solid ${colors.accent}`,
              backgroundColor: 'rgba(70, 187, 137, 0.12)',
              color: colors.textStrong,
              cursor: 'pointer'
            }}
          >
            Open repair handling
          </button>
        </div>
      )}
    </div>
  );
}
